import { type YValueType } from '@databases/collab/types';
import {
  type YAttachmentValue,
  YSingleAttachmentValue
} from '@databases/collab/y-attachment-value';
import { type YDatabase } from '@databases/collab/y-database';
import { ValueProperties, type YEntry } from '@databases/collab/y-entry';
import { FieldType } from '@databases/model/field';
import { v4 as generateId } from 'uuid';

const sanitizeFileId = (value: string) => {
  if (value.includes('confluence:')) {
    return value.split('confluence:')[1];
  }
  return value;
};

const sanitizeAttachmentValue = (entry: YEntry, fieldId: string, value: YValueType) => {
  if (value && value.type === FieldType.Attachment) {
    const maybeStringAttachment = value.unwrap().toJSON()[ValueProperties.value];
    if (typeof maybeStringAttachment === 'string') {
      if (maybeStringAttachment === '') {
        entry.setValue(fieldId, FieldType.Attachment).setTo([]);
      } else {
        const attachmentValue = YSingleAttachmentValue.fromJSON({
          fileId: sanitizeFileId(maybeStringAttachment),
          id: generateId()
        });
        entry.setValue(fieldId, FieldType.Attachment).setTo([attachmentValue]);
      }
    }

    const maybeAttachments = value.toJSON().value;
    if (Array.isArray(maybeAttachments)) {
      const yAttachment = value as YAttachmentValue;
      for (let index = maybeAttachments.length - 1; index >= 0; index--) {
        const attachment = maybeAttachments[index];
        if (attachment.fileId === '') {
          yAttachment.delete(index);
        } else {
          attachment.fileId = sanitizeFileId(attachment.fileId);
        }
      }
    }
  }
};

// Converts attachments if attachment value is string (legacy) and removes attachment if fileId is empty string
const sanitizeAttachments = (ydb: YDatabase, fieldId: string) => {
  const defaultValue = ydb.defaultEntry.getValue(fieldId);

  sanitizeAttachmentValue(ydb.defaultEntry, fieldId, defaultValue);

  for (const entryId of ydb.entries) {
    const entry = ydb.entriesById[entryId];
    const value = entry.getValue(fieldId);

    sanitizeAttachmentValue(entry, fieldId, value);
  }
};

/**
 * This migration transforms existing attachments by accessing the value property
 * of an entry value and resets it to a SingleAttachmentValue array
 * containing the accessed string value.
 */
export const transformAttachments = {
  version: 1727367120981, // timestamp of the migration when it's written
  apply(documentId: string, ydb: YDatabase) {
    for (const fieldId of ydb.fields) {
      const field = ydb.fieldsById[fieldId];
      if (field && field.type === FieldType.Attachment) {
        sanitizeAttachments(ydb, fieldId);
      }
    }
  }
};
